import * as React from "react";
import { observer } from "mobx-react";
import i18n from "i18n";
import { Grid, Typography, Button, withStyles } from "@material-ui/core";
import { Error as ErrorIcon } from "@material-ui/icons";
import { ModelMapStore } from "modules/modelMap/ModelMapStore";
import { ModelMap } from "modules/modelMap/models/ModelMap";
import { TakeOffStore } from "modules/takeOff/TakeOffStore";
import { LibraryStore } from "modules/library/LibraryStore";
import { Cube } from "common/icons";
import { EModelMapType } from "common/enums";
import { WBSStore } from "modules/WBS/WBSStore";

const styles = (theme) => ({
  modal: {
    left: 0,
    zIndex: 2,
    top: 0,
    height: "calc(100vh - " + 113 + "px)",
    width: "100%",
    backgroundColor: "rgba(0,0,0,0.5)",
    position: "absolute" as any,
    overflow: "hidden",
  },
  container: {
    top: 0,
    left: 0,
    zIndex: 2,
    height: "100%",
    width: "100%",
    display: "grid",
    gridTemplateRows: "auto 1fr",
    backgroundColor: "#FFF",
    position: "absolute" as any,
  },
  layerTitleContainer: {
    marginTop: 50,
    marginBottom: 44,
  },
  layerTitle: {
    fontSize: "12px",
    fontFamily: "Roboto",
    letterSpacing: "1.2px",
  },
  modelMapContainer: {
    display: "inline-block",
    marginBottom: 35,
    width: "100%",
  },
  modelMapTitle: {
    fontSize: "22px",
    fontFamily: "Roboto",
    display: "inline-block",
    verticalAlign: "middle",
    margin: "auto",
  },
  cube: {
    width: "35px",
    height: "35px",
    fontSize: "15px",
    marginRight: "8px",
    color: "#ABC200",
    display: "inline-block",
    verticalAlign: "middle",
  },
  textTitle: {
    fontFamily: "Roboto",
    fontSize: "12px",
    letterSpacing: "1.2px",
    color: "#000000DE",
    marginBottom: 15,
  },
  textSubTitle: {
    fontSize: "18px",
    fontFamily: "Roboto",
    marginBottom: 63,
    marginTop: 15,
  },
  itemContainer: {
    width: "70%",
    margin: "0 auto",
    marginBottom: "40px",
  },
  item: {
    borderRadius: "16px",
    opacity: 1,
    width: "fit-content",
    padding: "6px 10px",
    height: "fit-content",
    maxHeight: "32px",
    margin: "5px",
    display: "inline-flex",
  },
  itemIconContainer: { paddingRight: "3px", height: 20, float: "left" as any },
  itemIcon: { color: "#FB4436", fontSize: "20px" },
  button: {
    height: "36px",
    width: "106px",
    marginTop: 42,
    background: "#ABC200",
    "&:hover": {
      background: "#92A501",
    },
  },
});

const ListItems = ({ classes, list = [] }: { classes: any; list: any[] }) => (
  <div className={classes.itemContainer}>
    {list.map((it) => (
      <div
        className={classes.item}
        style={{
          background: it.error
            ? "#FFD8D8 0% 0% no-repeat padding-box"
            : "#E0E0E0 0% 0% no-repeat padding-box",
        }}
      >
        <div style={{ height: 20 }}>
          {it.error && (
            <div className={classes.itemIconContainer}>
              <ErrorIcon className={classes.itemIcon} />
            </div>
          )}
          {typeof it.name === "string" && it.name}
        </div>
      </div>
    ))}
  </div>
);

type IProps = {
  classes: any;
};

@observer
class LayerAddModelMapView extends React.Component<IProps> {
  state = {
    modelsFiltered: [{ name: "", error: false }] as any,
  };
  handleEdit = () => {
    TakeOffStore.layerTakeOffEdit = "edit";
  };

  async renderModelsList(modelMap?: ModelMap, allModels: boolean = false) {
    const models: any = [];
    if (modelMap && modelMap.files) {
      for (const fileId of modelMap.files) {
        let libFounded = LibraryStore.findLibrayById(fileId);
        if (libFounded) {
          let name = await LibraryStore.getLibrariesLabelName(libFounded);
          models.push({
            name,
            error: false,
          });
        }
      }
    }
    if (modelMap && modelMap.modelErrors) {
      for (const { label } of modelMap.modelErrors) {
        models.push({
          name: label,
          error: allModels ? false : true,
        });
      }
    }
    return models;
  }

  renderCategoriesList(modelMap?: ModelMap) {
    const typesList: any = [];
    if (modelMap && modelMap.types) {
      const listErrors = (modelMap && modelMap.categoryErrors) || [];
      for (const type of modelMap.types) {
        const idx = listErrors.findIndex((c) => c == type);
        typesList.push({
          name: type,
          error: idx !== -1,
        });
      }
    }
    return typesList;
  }

  async componentDidMount(): Promise<void> {
    const modelMap = TakeOffStore.takeOffModelMap
      ? TakeOffStore.takeOffModelMap.data
      : undefined;
    const allModels = modelMap
      ? modelMap.useAllModels == undefined || modelMap.useAllModels == true
      : false;
    this.setState({
      modelsFiltered: await this.renderModelsList(modelMap, allModels),
    });
  }
  render() {
    const { classes } = this.props;

    const modelMap = TakeOffStore.takeOffModelMap
      ? TakeOffStore.takeOffModelMap.data
      : undefined;
    const allModels = modelMap
      ? modelMap.useAllModels == undefined || modelMap.useAllModels == true
      : false;

    const typesList = this.renderCategoriesList(modelMap);
    const { modelsFiltered } = this.state;
    const isContructiveType =
      ModelMapStore.typeSelected === EModelMapType.ELEMENT_CONSTRUCTIVE;

    return (
      <div className={classes.modal}>
        <Grid container className={classes.container}>
          <div style={{ textAlign: "center" }}>
            <div className={classes.layerTitleContainer}>
              <Typography className={classes.layerTitle} component="h2">
                {isContructiveType
                  ? i18n.modelMaps.addLayer.headerECView
                  : i18n.modelMaps.addLayer.headerMatView}
              </Typography>
            </div>
            <div className={classes.modelMapContainer}>
              <Cube className={classes.cube} />
              <div className={classes.modelMapTitle}>
                {modelMap ? modelMap.name : ""}
              </div>
            </div>

            <div className={classes.textTitle}>
              {i18n.modelMaps.addLayer.modelsFiltered}
            </div>
            {allModels ? (
              <div className={classes.textSubTitle}>
                {i18n.modelMaps.addLayer.allModels}
              </div>
            ) : (
              <ListItems classes={classes} list={modelsFiltered} />
            )}
            <div className={classes.textTitle}>
              {isContructiveType
                ? i18n.modelMaps.addLayer.constructiveElements.toUpperCase()
                : i18n.modelMaps.addLayer.Materials.toUpperCase()}
            </div>
            <ListItems classes={classes} list={typesList} />
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={this.handleEdit}
            >
              {i18n.editto}
            </Button>
          </div>
        </Grid>
      </div>
    );
  }
}

export default withStyles(styles)(LayerAddModelMapView);



// WEBPACK FOOTER //
// ./src/modules/modelMap/components/layouts/LayerAddModelMapView.tsx